import React, { useEffect } from 'react'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/molecules/Table'
import { useInView } from 'react-intersection-observer'
import { Loader } from 'lucide-react'
import { JumpingDots } from 'src/components/atoms/JumpingDots'

interface ColumnMeta {
  headerClassName?: string
  cellClassName?: string
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  onLoadMore?: () => void
}

function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
}: DataTableProps<TData, TValue>) {
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && onLoadMore) {
      onLoadMore()
    }
  }, [inView, hasNextPage, isFetchingNextPage, onLoadMore])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="relative max-h-[calc(100vh-400px)] min-h-[200px] overflow-x-auto overflow-y-auto">
      {/* The outer box must own both scrollbars, or the horizontal one sits below the last row. */}
      <Table containerClassName="overflow-visible">
        <TableHeader className="sticky top-0 z-20 bg-gray-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className={(header.column.columnDef.meta as ColumnMeta)?.headerClassName}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className={(cell.column.columnDef.meta as ColumnMeta)?.cellClassName}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {isLoading ? (
                  <Loader className=" mx-auto h-6 w-6 animate-spin text-light-green-600" size={32} />
                ) : (
                  'No results.'
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Loader trigger */}
      {hasNextPage && (
        <div ref={ref} className="h-20 w-full">
          {isFetchingNextPage && <JumpingDots className="text-light-green-50" />}
        </div>
      )}
    </div>
  )
}

export default DataTable
